import { useEffect, useRef, useState } from 'react'
import { X, Cake } from 'lucide-react'
import { useLocation } from '@tanstack/react-router'
import { fetchUsersWithProfiles } from '@/lib/collections/workerProfile-Users/fetchCollaboratorsWithProfile'
import { storage } from '@/lib/appwrite'
import { useAuth } from '@/context/AuthContext'

type BirthdayPerson = { name: string; userId: string }

const BUCKET_ID = import.meta.env.VITE_IMAGE_BIRTHDAY_BUCKET_ID as string | undefined

const getEcuadorTodayMMDD = () =>
    new Date().toLocaleDateString('en-CA', { timeZone: 'America/Guayaquil' }).slice(5)

const getTodayKey = () =>
    `birthday-card-seen-${new Date().toLocaleDateString('en-CA', { timeZone: 'America/Guayaquil' })}`

let _cachedImageUrl: string | null | undefined = undefined
let _lastBirthdayCheckDate = ''

const getRandomImageUrl = async (): Promise<string | null> => {
    if (_cachedImageUrl !== undefined) return _cachedImageUrl
    if (!BUCKET_ID) return (_cachedImageUrl = null)
    try {
        const files = await storage.listFiles(BUCKET_ID)
        if (!files.files.length) return (_cachedImageUrl = null)
        const random = files.files[Math.floor(Math.random() * files.files.length)]
        return (_cachedImageUrl = storage.getFileView(BUCKET_ID, random.$id).toString())
    } catch {
        return (_cachedImageUrl = null)
    }
}

const formatNames = (names: string[]) => {
    if (names.length === 1) return names[0]
    return `${names.slice(0, -1).join(', ')} y ${names[names.length - 1]}`
}

const DashboardEvents = () => {
    const { pathname } = useLocation()
    const { user } = useAuth()
    const [birthdays, setBirthdays] = useState<BirthdayPerson[]>([])
    const [imageUrl, setImageUrl] = useState<string | null>(null)
    const [cardVisible, setCardVisible] = useState(false)
    const [dismissed, setDismissed] = useState(false)
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        if (!pathname.includes('dashboard')) return

        if (timerRef.current) clearTimeout(timerRef.current)

        const todayKey = getTodayKey()
        const alreadySeen = !!localStorage.getItem(todayKey)

        // Primera visita del día: fetch fresco. Resto del día: usa cache de 10 min
        const todayDate = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Guayaquil' })
        const forceRefresh = _lastBirthdayCheckDate !== todayDate
        if (forceRefresh) _lastBirthdayCheckDate = todayDate

        fetchUsersWithProfiles(forceRefresh)
            .then(users => {
                const todayMMDD = getEcuadorTodayMMDD()
                const bdays = users
                    .filter(u => {
                        if (!u.profile?.isActive) return false
                        if (!u.profile?.birthday) return false
                        const d = new Date(u.profile.birthday)
                        const mmdd = `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
                        return mmdd === todayMMDD
                    })
                    .map(u => ({
                        name: [u.profile?.firstName, u.profile?.lastName].filter(Boolean).join(' ') || u.name,
                        userId: u.id,
                    }))

                if (bdays.length === 0) return

                setBirthdays(bdays)

                if (alreadySeen) {
                    setDismissed(true)
                    setCardVisible(false)
                } else {
                    setDismissed(false)
                    timerRef.current = setTimeout(() => setCardVisible(true), 600)
                }

                // Imagen: solo se busca una vez por sesión
                getRandomImageUrl().then(url => setImageUrl(url))
            })
            .catch(() => null)

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current)
        }
    }, [pathname])

    const handleClose = () => {
        localStorage.setItem(getTodayKey(), '1')
        setCardVisible(false)
        setDismissed(true)
    }

    if (birthdays.length === 0) return null

    const nameLabel = formatNames(birthdays.map(b => b.name))
    const isMyCelebration = birthdays.some(b => b.userId === user?.$id)

    // Cumpleañeros que NO son el usuario actual (para el mensaje general)
    const othersLabel = formatNames(birthdays.filter(b => b.userId !== user?.$id).map(b => b.name))

    return (
        <>
            {dismissed && !cardVisible && (
                <button
                    onClick={() => setCardVisible(true)}
                    title={isMyCelebration ? '¡Es tu cumpleaños!' : `Cumpleaños de ${nameLabel}`}
                    className="fixed bottom-40 right-4 z-40 rounded-full bg-rose-500 p-2 text-white shadow-lg hover:bg-rose-600 transition-colors cursor-pointer"
                >
                    <Cake className="h-4 w-4" />
                </button>
            )}

            {cardVisible && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-in fade-in duration-300"
                    onClick={handleClose}
                >
                    <div
                        className="relative w-full max-w-sm overflow-hidden rounded-3xl shadow-2xl animate-in zoom-in-95 duration-300"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="relative h-56 w-full bg-rose-50 dark:bg-zinc-800">
                            {imageUrl ? (
                                <img src={imageUrl} alt="Cumpleaños" className="h-full w-full object-cover" />
                            ) : (
                                <div className="flex h-full items-center justify-center text-8xl select-none">🎂</div>
                            )}
                            <button
                                onClick={handleClose}
                                className="absolute top-3 right-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/30 text-white hover:bg-black/50 transition-colors cursor-pointer backdrop-blur-sm"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        <div className="bg-white dark:bg-zinc-900 px-6 py-5 font-outfit">
                            {isMyCelebration ? (
                                <>
                                    <p className="text-xs font-medium uppercase tracking-widest text-rose-400 mb-1">
                                        ¡Hoy es tu día!
                                    </p>
                                    <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                                        🎉 ¡Feliz cumpleaños!
                                    </h2>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                                        Que este nuevo año esté lleno de éxitos, salud y alegría. ¡El equipo te desea lo mejor! 🥳
                                    </p>
                                    {othersLabel && (
                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                                            También celebra hoy: {othersLabel}
                                        </p>
                                    )}
                                </>
                            ) : (
                                <>
                                    <p className="text-xs font-medium uppercase tracking-widest text-rose-400 mb-1">
                                        ¡Hoy es un día especial!
                                    </p>
                                    <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                                        🎂 {nameLabel}
                                    </h2>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                                        {birthdays.length === 1
                                            ? 'Está de cumpleaños. Dale tus mejores deseos.'
                                            : 'Están de cumpleaños. Dales tus mejores deseos.'}
                                    </p>
                                </>
                            )}
                            <button
                                onClick={handleClose}
                                className="mt-5 w-full rounded-2xl bg-rose-500 hover:bg-rose-600 active:bg-rose-700 text-white text-sm font-semibold py-3 transition-colors cursor-pointer"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default DashboardEvents
